import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
  GetApp,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import AnalyticsOverview from '../components/course-dashboard/AnalyticsOverview';
import EnrollmentAnalytics from '../components/course-dashboard/EnrollmentAnalytics';
import ProgressAnalytics from '../components/course-dashboard/ProgressAnalytics';
import PerformanceAnalytics from '../components/course-dashboard/PerformanceAnalytics';
import TopicCoverageAnalytics from '../components/course-dashboard/TopicCoverageAnalytics';
import StudentActivityAnalytics from '../components/course-dashboard/StudentActivityAnalytics';
import AttemptAnalytics from '../components/course-dashboard/AttemptAnalytics';
import ImprovementRecommendations from '../components/course-dashboard/ImprovementRecommendations';
import SuccessAreas from '../components/course-dashboard/SuccessAreas';
import GamificationManagement from '../components/course-dashboard/GamificationManagement';
import AwardsCertificatesManagement from '../components/course-dashboard/AwardsCertificatesManagement';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const CourseDashboard: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Mock course data
  const courseName = 'Mathematics S1';

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    setLastRefresh(new Date());
    // In real implementation, this would trigger data refresh
  };

  const handleExport = () => {
    // In real implementation, this would export dashboard data
    console.log('Exporting dashboard data...');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
            <Link
              color="inherit"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate('/admin/courses');
              }}
              sx={{ cursor: 'pointer' }}
            >
              Courses
            </Link>
            <Typography color="text.primary">{courseName}</Typography>
            <Typography color="text.primary">Performance Dashboard</Typography>
          </Breadcrumbs>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate('/admin/courses')}>
              <ArrowBack />
            </IconButton>
            <DashboardIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              {courseName} - Dashboard
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={handleExport}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Last Refresh Indicator */}
      <Box mb={2}>
        <Typography variant="caption" color="text.secondary">
          Last refreshed: {lastRefresh.toLocaleString()}
        </Typography>
      </Box>

      {/* Summary Cards - Always Visible */}
      <AnalyticsOverview courseId={courseId || '1'} />

      {/* Tabs */}
      <Paper sx={{ mt: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Enrollment" />
          <Tab label="Progress" />
          <Tab label="Performance" />
          <Tab label="Topic Coverage" />
          <Tab label="Student Activity" />
          <Tab label="Attempts" />
          <Tab label="Improvements" />
          <Tab label="Success Areas" />
          <Tab label="Gamification" />
          <Tab label="Awards & Certificates" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <EnrollmentAnalytics courseId={courseId || '1'} />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <ProgressAnalytics courseId={courseId || '1'} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <PerformanceAnalytics courseId={courseId || '1'} />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <TopicCoverageAnalytics courseId={courseId || '1'} />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <StudentActivityAnalytics courseId={courseId || '1'} />
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <AttemptAnalytics courseId={courseId || '1'} />
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          <ImprovementRecommendations courseId={courseId || '1'} />
        </TabPanel>

        <TabPanel value={tabValue} index={7}>
          <SuccessAreas courseId={courseId || '1'} />
        </TabPanel>

        <TabPanel value={tabValue} index={8}>
          <GamificationManagement courseId={courseId || '1'} />
        </TabPanel>

        <TabPanel value={tabValue} index={9}>
          <AwardsCertificatesManagement courseId={courseId || '1'} />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default CourseDashboard;

